import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getRentalCustomerProfile } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportDate, fmtExportMoney, fmtExportStatus } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const statusColor = (status) => {
  const map = { Active: 'success', Overdue: 'error', Completed: 'info', Cancelled: 'default' };
  return map[status] || 'default';
};

const RentalRows = ({ rows, navigate, empty }) => (
  rows.length === 0 ? (
    <Typography color="text.secondary">{empty}</Typography>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Rental</TableCell>
          <TableCell>Items</TableCell>
          <TableCell>Period</TableCell>
          <TableCell align="right">Balance</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/rentals/${r.id}`)}>
            <TableCell>{r.rental_number}</TableCell>
            <TableCell>{(r.items || []).map((it) => it.product_name).join(', ') || '—'}</TableCell>
            <TableCell>{r.start_date || r.rental_date} → {r.end_date || r.return_date}</TableCell>
            <TableCell align="right">{money(r.balance)}</TableCell>
            <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
);

const RentalCustomerProfile = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getRentalCustomerProfile(customerId);
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load customer profile'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress />;
  if (error && !data) return <Alert severity="error">{error}</Alert>;

  const c = data.customer || {};
  const s = data.summary || {};
  const current = data.current || [];
  const rentals = data.rentals || [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/rentals/customers')} sx={{ mb: 2 }}>
        All rental customers
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{c.name || 'Customer'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[c.customer_code, c.phone, c.email, c.company_name].filter(Boolean).join(' · ') || 'No contact details'}
          </Typography>
          {c.address && <Typography variant="body2" color="text.secondary">{c.address}</Typography>}
        </Box>
        <ExportButtons
          title={`Rental customer ${c.name || customerId}`}
          filename={`rental_customer_${customerId}`}
          columns={[
            { label: 'Rental', value: (r) => dash(r.rental_number) },
            { label: 'Start', value: (r) => fmtExportDate(r.start_date || r.rental_date) },
            { label: 'End', value: (r) => fmtExportDate(r.end_date || r.return_date) },
            { label: 'Status', value: (r) => fmtExportStatus(r.status) },
            { label: 'Balance', value: (r) => fmtExportMoney(r.balance) }
          ]}
          rows={rentals}
        />
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'All rentals', value: s.total_rentals || 0 },
          { label: 'Current', value: s.current_rentals || 0 },
          { label: 'Completed', value: s.completed_rentals || 0 },
          { label: 'Billed', value: money(s.total_billed) },
          { label: 'Paid', value: money(s.total_paid) },
          { label: 'Balance', value: money(s.balance_due) }
        ].map((card) => (
          <Grid item xs={6} md={2} key={card.label}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>Current rentals</Typography>
        <RentalRows rows={current} navigate={navigate} empty="No current rentals." />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>All rentals</Typography>
        <RentalRows rows={rentals} navigate={navigate} empty="No rentals on file." />
      </Paper>
    </Box>
  );
};

export default RentalCustomerProfile;
